import {useState} from 'react';
import {Popover} from '@mui/material';

import {BANDWIDTH_THRESHOLDS, BW_SENTINEL_CAP_KBPS, QUALITY_CONFIG} from './constants';
import type {QualityLevel} from './types';
import {useNetworkQuality} from './useNetworkQuality';
import {formatBitrate, getQualityLevel} from './utils';

import styles from './networkQuality.module.scss';

const StatRow = ({label, value}: {label: string; value: string | undefined}) =>
    value !== undefined ? (
        <div className={styles.stat}>
            <span className={styles.label}>{label}</span>
            <span className={styles.value}>{value}</span>
        </div>
    ) : null;

export const NetworkQualityIndicator = () => {
    const stats = useNetworkQuality();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const quality = getQualityLevel(stats);
    const {color, label, Icon} = QUALITY_CONFIG[quality];
    const open = Boolean(anchorEl);

    // Direction-level quality for receive/send section headers in popover
    const bwQuality = (kbps: number | undefined): QualityLevel => {
        if (!kbps) return 'unknown';
        const capped = Math.min(kbps, BW_SENTINEL_CAP_KBPS);
        if (capped >= BANDWIDTH_THRESHOLDS.fairBelow) return 'good';
        if (capped >= BANDWIDTH_THRESHOLDS.poorBelow) return 'fair';
        return 'poor';
    };
    const {color: receiveColor, Icon: RecvIcon} = QUALITY_CONFIG[bwQuality(stats.receiveBitRateAvailable)];
    const {color: sendColor, Icon: SendIcon} = QUALITY_CONFIG[bwQuality(stats.sendBitRateAvailable)];

    const fmt = (n: number | undefined, unit: string, decimals = 0) => {
        if (n === undefined) return undefined;
        const value = decimals ? n.toFixed(decimals) : Math.round(n);
        return `${value} ${unit}`;
    };

    const d = {
        decodeBwPct: fmt(stats.availableDecodeBwPercent, '%'),
        encodeBwPct: fmt(stats.availableEncodeBwPercent, '%'),
        videoActual: formatBitrate(stats.videoActualBitRate),
        videoTarget: formatBitrate(stats.videoTargetBitRate),
        videoDropPct: fmt(stats.videoDropPct, '%', 1),
        localVideoFps: fmt(stats.localVideoFps, 'fps'),
        localQualityLimit: stats.localQualityLimit,
        videoCodec: stats.videoCodec,
        audioActual: formatBitrate(stats.audioActualBitRate),
        audioCodec: stats.audioCodec,
        audioRtt: fmt(stats.audioRtt, 'ms'),
        rtt: fmt(stats.rtt, 'ms'),
        transport: stats.transport,
    };

    return (
        <>
            <button
                className={styles.indicatorButton}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                title={`Network quality: ${label}`}
                aria-label="Network quality indicator"
            >
                <Icon style={{color, fontSize: 24}} />
                <span className={styles.qualityLabel} style={{color}}>
                    {label}
                </span>
            </button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                PaperProps={{className: styles.popoverPaper}}
            >
                <div className={styles.popoverContent}>
                    <div className={styles.popoverHeader}>
                        <div className={styles.popoverHeaderLeft}>
                            <Icon style={{color, fontSize: 18}} />
                            <span style={{color}}>Network Quality: {label}</span>
                        </div>
                        {d.rtt !== undefined && <span className={styles.rttBadge}>RTT {d.rtt}</span>}
                    </div>
                    <div className={styles.statsList}>
                        {/* ── Poor quality banner ── */}
                        {quality === 'poor' && stats.localQualityLimit && (
                            <div className={styles.warningBanner}>⚠ Video limited by {stats.localQualityLimit}</div>
                        )}

                        {/* ── Participants ── */}
                        {stats.participants && stats.participants.length > 0 && (
                            <div className={styles.participantTable}>
                                <div className={styles.participantTableHead}>
                                    <span className={styles.pColName}>Name</span>
                                    <span className={styles.pColQuality}>Quality</span>
                                    <span className={styles.pColNum}>V-BR</span>
                                    <span className={styles.pColNum}>V-Lost</span>
                                    <span className={styles.pColNum}>V-Conc</span>
                                    <span className={styles.pColNum}>A-BR</span>
                                    <span className={styles.pColNum}>A-Lost</span>
                                    <span className={styles.pColNum}>A-Conc</span>
                                    <span className={styles.pColNum}>Jitter</span>
                                </div>
                                {stats.participants.map((p) => {
                                    const qColor = QUALITY_CONFIG[p.quality]?.color ?? '#9e9e9e';
                                    const fmtBps = (bps?: number) => {
                                        if (bps == null) return '—';
                                        const k = bps / 1000;
                                        return k >= 1 ? `${Math.round(k)}k` : `${Math.round(bps)}`;
                                    };
                                    return (
                                        <div key={p.id} className={styles.participantTableRow}>
                                            <span className={styles.pColName} title={p.name}>
                                                {p.name}
                                            </span>
                                            <span className={styles.pColQuality} style={{color: qColor}}>
                                                {QUALITY_CONFIG[p.quality]?.label ?? p.quality}
                                            </span>
                                            <span className={styles.pColNum}>{fmtBps(p.videoRxBitRate)}</span>
                                            <span className={styles.pColNum}>{p.videoPacketsLost ?? '—'}</span>
                                            <span className={styles.pColNum}>{p.videoPacketsConcealed ?? '—'}</span>
                                            <span className={styles.pColNum}>{fmtBps(p.micRxBitRate)}</span>
                                            <span className={styles.pColNum}>{p.micPacketsLost ?? '—'}</span>
                                            <span className={styles.pColNum}>{p.micPacketsConcealed ?? '—'}</span>
                                            <span className={styles.pColNum}>
                                                {p.micJitter != null ? `${(p.micJitter * 1000).toFixed(0)}ms` : '—'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ── Bandwidth ── */}
                        <div className={styles.sectionHeader}>Own Bandwidth Info</div>
                        <div className={styles.directionHeader}>
                            <RecvIcon style={{color: receiveColor, fontSize: 14}} />
                            <span style={{color: receiveColor}}>Receiving</span>
                        </div>
                        <StatRow label="Available" value={formatBitrate(stats.receiveBitRateAvailable)} />
                        <StatRow label="Total" value={formatBitrate(stats.receiveBitRateTotal)} />
                        <StatRow label="BW Available" value={d.decodeBwPct} />
                        <div className={styles.directionHeader}>
                            <SendIcon style={{color: sendColor, fontSize: 14}} />
                            <span style={{color: sendColor}}>Sending</span>
                        </div>
                        <StatRow label="Available" value={formatBitrate(stats.sendBitRateAvailable)} />
                        <StatRow label="Total" value={formatBitrate(stats.sendBitRateTotal)} />
                        <StatRow label="BW Available" value={d.encodeBwPct} />

                        {/* ── Video ── */}
                        <div className={styles.sectionHeader}>Video</div>
                        <StatRow label="Codec" value={d.videoCodec} />
                        <StatRow label="Actual BR" value={d.videoActual} />
                        <StatRow label="Target BR" value={d.videoTarget} />
                        <StatRow label="Pkt Drop" value={d.videoDropPct} />
                        <StatRow label="Local FPS" value={d.localVideoFps} />
                        <StatRow label="Quality Limit" value={d.localQualityLimit} />

                        {/* ── Audio ── */}
                        <div className={styles.sectionHeader}>Audio</div>
                        <StatRow label="Codec" value={d.audioCodec} />
                        <StatRow label="Actual BR" value={d.audioActual} />
                        <StatRow label="RTT" value={d.audioRtt} />

                        {/* ── Transport ── */}
                        <div className={styles.sectionHeader}>Transport</div>
                        <StatRow label="Type" value={d.transport} />
                    </div>
                </div>
            </Popover>
        </>
    );
};
